/**
 * Reading design tokens as colours for canvas code (kinetic dots, candle charts). Colours always
 * come from tokens.css; nothing here defines one.
 */

/**
 * A token's colour as [r, g, b, a]. The browser normalises it through a probe element's computed
 * colour, so any token syntax works (the CSS minifier rewrites rgba() as 8-digit hex).
 */
export function tokenChannels(token: string): [number, number, number, number] {
  const probe = document.createElement('span');
  probe.style.color = `var(${token})`;
  probe.hidden = true;
  document.body.append(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();
  const parts =
    /rgba?\(([^)]+)\)/
      .exec(computed)?.[1]
      ?.split(/[\s,/]+/)
      .filter(Boolean) ?? [];
  const [r = 0, g = 0, b = 0, a = 1] = parts.map(Number);
  return [r, g, b, a];
}

/** A token as a canvas colour string, optionally at a fraction of its opacity. */
export function tokenColor(token: string, opacity = 1): string {
  const [r, g, b, a] = tokenChannels(token);
  return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(a * opacity)})`;
}

/** Calls `onChange` whenever the theme switches; returns the unsubscribe function. */
export function watchTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributeFilter: ['data-theme'] });
  return () => {
    observer.disconnect();
  };
}
